"use client";

import { useState, useRef } from "react";
import {
  Upload,
  X,
  Image as ImageIcon,
  Video,
  Star,
  AlertCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { createClient } from "@/lib/supabase/client";

interface MediaItem {
  url: string;
  type: "image" | "video" | "360";
  is_primary: boolean;
}

interface ImageUploadProps {
  media: MediaItem[];
  onChange: (media: MediaItem[]) => void;
  maxFiles?: number;
  acceptedTypes?: string[];
}

export default function ImageUpload({
  media,
  onChange,
  maxFiles = 10,
  acceptedTypes = [
    "image/jpeg",
    "image/png",
    "image/webp",
    "video/mp4",
    "video/webm",
    "video/quicktime",
    "video/avi",
    "video/mov",
  ],
}: ImageUploadProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadingFile, setUploadingFile] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    console.log("File select triggered:", files?.length, "files");
    if (!files || files.length === 0) return;

    const remainingSlots = maxFiles - media.length;
    console.log(
      "Remaining slots:",
      remainingSlots,
      "of",
      maxFiles,
      "current media:",
      media.length,
    );
    if (files.length > remainingSlots) {
      setError(`You can only upload ${remainingSlots} more file(s)`);
      return;
    }

    setError(null);
    setIsUploading(true);
    console.log("Starting upload process...");

    // Disable hot reload during upload in development
    if (process.env.NODE_ENV === "development") {
      console.warn(
        "🚨 IMPORTANT: Do not save files while upload is in progress to avoid interruption",
      );
    }

    try {
      console.log("Creating Supabase client...");
      const supabase = createClient();

      // Check if user is authenticated
      console.log("Checking user authentication...");

      // Get and validate the user (this also refreshes the session if needed)
      const {
        data: { user },
        error: authError,
      } = await supabase.auth.getUser();

      if (authError || !user) {
        console.error("Authentication error:", authError);
        setError(
          `Authentication failed: ${authError?.message || "You must be logged in to upload files"}`,
        );
        return;
      }

      console.log("User authenticated for upload:", user.email);
      console.log("User details:", {
        userId: user.id,
        userEmail: user.email,
      });

      const newMedia: MediaItem[] = [];
      console.log("Starting file processing loop...");

      for (const file of Array.from(files)) {
        setUploadingFile(`${file.name} (0%)`);

        // Check if file type is accepted
        console.log("File validation:", {
          fileName: file.name,
          fileType: file.type,
          fileSize: file.size,
          fileSizeMB: (file.size / (1024 * 1024)).toFixed(2) + "MB",
          acceptedTypes: acceptedTypes,
          isAccepted: acceptedTypes.includes(file.type),
          isVideo: file.type.startsWith("video"),
        });

        // Check file type - be more lenient with video files since browsers may report different MIME types
        const isImage = file.type.startsWith("image/");
        const isVideo = file.type.startsWith("video/");
        const hasValidExtension =
          /\.(jpg|jpeg|png|webp|mp4|webm|mov|avi)$/i.test(file.name);

        if (
          !acceptedTypes.includes(file.type) &&
          !isImage &&
          !isVideo &&
          !hasValidExtension
        ) {
          setError(
            `File type ${file.type} is not accepted. Please upload images (JPG, PNG, WebP) or videos (MP4, WebM, MOV, AVI).`,
          );
          continue;
        }

        if (!isImage && !isVideo) {
          setError(
            `Please upload only image or video files. File type: ${file.type}`,
          );
          continue;
        }

        // Check file size - different limits for different types
        const maxFileSize = isVideo ? 50 * 1024 * 1024 : 10 * 1024 * 1024; // 50MB for video, 10MB for images
        const maxSizeText = isVideo ? "50MB" : "10MB";

        if (file.size > maxFileSize) {
          setError(
            `File "${file.name}" is too large. Maximum size for ${isVideo ? "videos" : "images"} is ${maxSizeText}.`,
          );
          continue;
        }

        // Upload to Supabase Storage with progress tracking
        const fileExt = file.name.split(".").pop()?.toLowerCase() || "jpg";
        const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
        const filePath = `studio-media/${fileName}`;

        // Ensure we're still authenticated before upload
        const {
          data: { user: currentUser },
        } = await supabase.auth.getUser();
        if (!currentUser) {
          console.error("Lost authentication during upload process");
          setError(
            "Session expired during upload. Please refresh the page and try again.",
          );
          break;
        }

        console.log("Uploading file:", {
          name: file.name,
          type: file.type,
          size: file.size,
          path: filePath,
          maxSize: maxFileSize,
          isVideo: isVideo,
        });

        try {
          setUploadingFile(`${file.name}...`);

          // Special handling for video files
          const isVideo = file.type.startsWith("video");
          const uploadTimeout = isVideo ? 180000 : 30000; // 3 minutes for videos, 30 seconds for images

          // Upload options
          const uploadOptions = {
            cacheControl: "3600",
            upsert: true,
            contentType: file.type,
          };

          // First check if bucket exists and is accessible
          console.log("Checking bucket access...");
          const { data: buckets, error: bucketError } =
            await supabase.storage.listBuckets();
          console.log(
            "Available buckets:",
            buckets?.map((b) => b.name),
          );

          if (bucketError) {
            console.error("Bucket listing error:", bucketError);
          }

          // Upload with timeout
          console.log("Starting upload to studio-images bucket...");
          const uploadPromise = supabase.storage
            .from("studio-images")
            .upload(filePath, file, uploadOptions);

          const timeoutPromise = new Promise((_, reject) =>
            setTimeout(
              () =>
                reject(
                  new Error(
                    isVideo
                      ? "Video upload is taking too long. The file might be too large (max 50MB) or your connection might be slow."
                      : "Upload is taking too long. Please try with a smaller file or check your connection.",
                  ),
                ),
              uploadTimeout,
            ),
          );

          const result = (await Promise.race([
            uploadPromise,
            timeoutPromise,
          ])) as { data: any; error: any };

          if (result.error) {
            console.error("Storage upload error:", {
              error: result.error,
              message: result.error.message,
              code: result.error.code,
              details: result.error.details,
              hint: result.error.hint,
              file: file.name,
              fileType: file.type,
              fileSize: file.size,
              isVideo: file.type.startsWith("video"),
              bucket: "studio-images",
              path: filePath,
              uploadTimeout: uploadTimeout,
            });

            let errorMessage = result.error.message;

            // Handle specific error cases
            if (result.error.message === "The request was aborted") {
              errorMessage = file.type.startsWith("video")
                ? "Video upload was cancelled. Large videos may take longer to upload. Please try with a smaller file or check your connection."
                : "Upload was cancelled. Please try again with a smaller file.";
            } else if (result.error.message?.includes("timeout")) {
              errorMessage = file.type.startsWith("video")
                ? "Video upload timed out. Please try with a smaller video file."
                : "Upload timed out. Please try with a smaller file.";
            } else if (
              result.error.message?.includes("row-level security") ||
              result.error.message?.includes("policy")
            ) {
              errorMessage =
                "You do not have permission to upload files. Please contact your administrator.";
            } else if (
              result.error.message?.includes("Invalid JWT") ||
              result.error.message?.includes("JWT")
            ) {
              errorMessage =
                "Your session has expired. Please refresh the page and try again.";
            } else if (result.error.statusCode === 413) {
              errorMessage = `File is too large. Maximum size is ${isVideo ? "50MB" : "10MB"}.`;
            } else if (
              result.error.message?.includes("Bucket not found") ||
              result.error.statusCode === 404
            ) {
              errorMessage =
                "Storage bucket not found. Please contact your administrator to set up file storage.";
              console.error('Storage bucket "studio-images" does not exist');
            }

            setError(`Upload failed: ${errorMessage}`);
            continue;
          }

          if (!result.data) {
            setError("Upload failed: No data returned from storage");
            continue;
          }

          // Get public URL
          const { data: urlData } = supabase.storage
            .from("studio-images")
            .getPublicUrl(filePath);

          if (!urlData?.publicUrl) {
            setError("Failed to get public URL for uploaded file");
            continue;
          }

          // Determine media type based on file type
          const mediaType = file.type.startsWith("video") ? "video" : "image";

          console.log("Created media item:", {
            url: urlData.publicUrl,
            type: mediaType,
            fileType: file.type,
            fileName: file.name,
          });

          newMedia.push({
            url: urlData.publicUrl,
            type: mediaType,
            is_primary: media.length === 0 && newMedia.length === 0,
          });
        } catch (error: any) {
          console.error("Upload error:", error);
          setError(`Failed to upload ${file.name}: ${error.message}`);
          continue;
        }
      }

      if (newMedia.length > 0) {
        onChange([...media, ...newMedia]);
      }
    } catch (err: any) {
      console.error("Upload error caught in outer try block:", err);
      console.error("Error stack:", err.stack);
      console.error("Error details:", JSON.stringify(err, null, 2));

      // Check if this is a development hot reload interruption
      if (
        err.message?.includes("timeout") ||
        err.message?.includes("aborted")
      ) {
        setError("Upload was interrupted. Please try again.");
      } else {
        setError(err.message || "Failed to upload files");
      }
    } finally {
      console.log("Upload process finished, cleaning up...");
      setIsUploading(false);
      setUploadingFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleRemove = (index: number) => {
    const newMedia = media.filter((_, i) => i !== index);

    // If we removed the primary image, make the first one primary
    if (media[index].is_primary && newMedia.length > 0) {
      newMedia[0].is_primary = true;
    }

    onChange(newMedia);
  };

  const handleSetPrimary = (index: number) => {
    const newMedia = media.map((item, i) => ({
      ...item,
      is_primary: i === index,
    }));
    onChange(newMedia);
  };

  const getFileIcon = (type: string) => {
    switch (type) {
      case "video":
        return <Video className="h-8 w-8" />;
      case "360":
        return <div className="text-xs font-bold">360°</div>;
      default:
        return <ImageIcon className="h-8 w-8" />;
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);

    const files = e.dataTransfer.files;
    console.log("Files dropped:", files.length);

    if (files.length > 0) {
      // Create a synthetic event to reuse handleFileSelect
      const syntheticEvent = {
        target: { files },
      } as React.ChangeEvent<HTMLInputElement>;

      await handleFileSelect(syntheticEvent);
    }
  };

  const handleUploadClick = () => {
    console.log("Upload area clicked");
    fileInputRef.current?.click();
  };

  return (
    <div className="space-y-4">
      {/* Upload Area */}
      <Card
        className={`border-2 border-dashed transition-colors ${
          isDragOver ? "border-primary bg-primary/5" : "border-border"
        } ${isUploading ? "opacity-50 pointer-events-none" : ""}`}
      >
        <div
          className="p-6"
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={acceptedTypes.join(",") + ",video/*,image/*"}
            onChange={handleFileSelect}
            className="hidden"
            disabled={isUploading}
          />

          <div
            className="text-center cursor-pointer"
            onClick={handleUploadClick}
          >
            {isUploading ? (
              <div className="flex flex-col items-center">
                <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin mb-4"></div>
                <p className="text-sm font-medium mb-1">
                  {uploadingFile || "Uploading..."}
                </p>
              </div>
            ) : (
              <>
                <Upload className="h-10 w-10 mx-auto mb-4 text-muted-foreground" />
                <p className="text-sm font-medium mb-1">
                  Click to upload or drag and drop
                </p>
                <p className="text-xs text-muted-foreground">
                  JPG, PNG, WebP (10MB max), MP4/WebM (50MB max) - {maxFiles}{" "}
                  files max
                </p>
              </>
            )}
          </div>
        </div>
      </Card>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3">
          <div className="flex items-center gap-2 text-red-800">
            <AlertCircle className="h-4 w-4" />
            <span className="text-sm">{error}</span>
          </div>
        </div>
      )}

      {/* Media Grid */}
      {media.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {media.map((item, index) => (
            <div key={index} className="relative group">
              <Card className="overflow-hidden">
                <div className="aspect-square bg-muted relative">
                  {item.type === "image" ? (
                    <img
                      src={item.url}
                      alt={`Upload ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  ) : item.type === "video" ? (
                    <div className="relative w-full h-full bg-gray-100">
                      <video
                        src={item.url}
                        className="w-full h-full object-cover"
                        controls
                        preload="metadata"
                        crossOrigin="anonymous"
                        playsInline
                        onError={(e) => {
                          console.error("Video load error:", e);
                          console.log("Video URL:", item.url);
                        }}
                        onLoadStart={() =>
                          console.log("Video loading started:", item.url)
                        }
                        onCanPlay={() =>
                          console.log("Video can play:", item.url)
                        }
                      >
                        Your browser does not support video playback.
                      </video>
                      <div className="absolute top-2 right-2 bg-black/50 text-white px-2 py-1 rounded text-xs">
                        VIDEO
                      </div>
                      {/* Debug overlay */}
                      <div className="absolute bottom-2 left-2 bg-blue-500 text-white px-1 py-0.5 rounded text-xs opacity-75">
                        {item.type} - {item.url.split("/").pop()?.slice(0, 15)}
                        ...
                      </div>
                    </div>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                      {getFileIcon(item.type)}
                    </div>
                  )}

                  {/* Primary Badge */}
                  {item.is_primary && (
                    <div className="absolute top-2 left-2 bg-primary text-primary-foreground px-2 py-1 rounded-md text-xs font-medium flex items-center gap-1">
                      <Star className="h-3 w-3" />
                      Primary
                    </div>
                  )}

                  {/* Actions */}
                  <div
                    className={`absolute inset-0 ${item.type === "video" ? "bg-black/30" : "bg-black/50"} opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2 pointer-events-none`}
                  >
                    <div className="pointer-events-auto flex gap-2">
                      {!item.is_primary && (
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => handleSetPrimary(index)}
                        >
                          <Star className="h-4 w-4" />
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleRemove(index)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
